import { faSparkles } from "@fortawesome/pro-solid-svg-icons";
import { Box, Stack } from "@mui/material";
import { keyframes, styled } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { useSessionStorage } from "usehooks-ts";

import { Icon, Typography } from "@/components/ui-v2";
import { useTrackNextAppSwitch } from "@/hooks";
import { getWebAppLoginUrl } from "@/utils/env";

/** Session-storage key: set once the card has played its glow in this tab. */
const GLOW_SHOWN_KEY = "layer.nextAppCardGlowShown";

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(164, 140, 253, 0); }
  50% { box-shadow: 0 0 0 4px rgba(164, 140, 253, 0.5); }
`;

const Root = styled("a", {
  shouldForwardProp: (prop) => prop !== "isGlowing",
})<{ isGlowing: boolean }>(({ theme, isGlowing }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  margin: theme.spacing(0, 2),
  padding: theme.spacing(2, 2.5),
  borderRadius: theme.spacing(2),
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #7B2FF7 100%)`,
  color: theme.palette.common.white,
  textDecoration: "none",
  cursor: "pointer",
  transition: "filter 150ms ease, transform 150ms ease",
  "&:hover": {
    filter: "brightness(1.1)",
    transform: "translateY(-1px)",
  },
  "&:focus-visible": {
    outline: `2px solid ${theme.palette.primary.light}`,
    outlineOffset: 2,
  },
  ...(isGlowing && {
    animation: `${glow} 1.4s ease-in-out 2`,
  }),
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
    transition: "none",
    "&:hover": {
      transform: "none",
    },
  },
}));

const IconWell = styled(Box)(({ theme }) => ({
  width: 28,
  height: 28,
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: theme.spacing(1.5),
  backgroundColor: "rgba(255, 255, 255, 0.18)",
}));

/**
 * The sidebar's permanent way into the new app. It glows on its first render
 * in a browser tab so it draws the eye once, and stays quiet after that.
 */
export const NextAppCard = () => {
  const trackNextAppSwitch = useTrackNextAppSwitch();
  const [hasGlowShown, setHasGlowShown] = useSessionStorage(
    GLOW_SHOWN_KEY,
    false,
  );
  const [isGlowing] = useState(() => !hasGlowShown);

  useEffect(() => {
    setHasGlowShown(true);
  }, [setHasGlowShown]);

  const onOpenNextApp = () => {
    trackNextAppSwitch("sidebar");
  };

  return (
    <Root
      href={getWebAppLoginUrl()}
      onClick={onOpenNextApp}
      isGlowing={isGlowing}
    >
      <IconWell>
        <Icon icon={faSparkles} />
      </IconWell>
      <Stack sx={{ flex: 1, minWidth: 0, gap: 0.5 }}>
        <Typography
          variant="small"
          fontWeight={700}
          sx={{
            color: "common.white",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          Try the new Layer
        </Typography>
        <Typography variant="micro" sx={{ color: "rgba(255, 255, 255, 0.8)" }}>
          More models, faster experience, more ways to create.
        </Typography>
      </Stack>
    </Root>
  );
};
